import { COLORS, COLOR_ORDER } from '../core/cube-config.js';
import { orientationInstruction4x4 } from '../core/orientation-4x4.js';
import { formatSolverDiagnostic4x4 } from '../solver/solver-diagnostics-4x4.js';

function escapeHtml(value) {
  return String(value)
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;');
}

function solverDiagnosticsTemplate(diagnostics = [], hidden = false) {
  const log = diagnostics.map(formatSolverDiagnostic4x4).join('\n');
  return `<details class="solver-diagnostics" data-solver-diagnostics${hidden ? ' hidden' : ''} open>
    <summary>求解诊断日志</summary>
    <pre data-solver-log>${escapeHtml(log)}</pre>
  </details>`;
}

function paletteTemplate(selectedColor) {
  return COLOR_ORDER.map((color) => {
    const item = COLORS[color];
    const active = color === selectedColor ? ' is-selected' : '';
    return `<button class="color-swatch${active}" type="button" data-color="${color}" aria-pressed="${color === selectedColor}" aria-label="选择${item.name}">
      <span style="--swatch: ${item.hex}"></span><b>${item.name}</b>
    </button>`;
  }).join('');
}

export function appShellTemplate() {
  return `
    <header class="site-header">
      <button class="brand" type="button" data-action="home" aria-label="返回首页">
        <span class="brand-mark" aria-hidden="true"><i style="--brand-color:${COLORS.white.hex}"></i><i style="--brand-color:${COLORS.red.hex}"></i><i style="--brand-color:${COLORS.blue.hex}"></i></span>
        <span>复原轨迹</span>
      </button>
      <div class="header-actions">
        <span class="phase-badge">v1.2.2 · 2×2 / 3×3 / 4×4</span>
        <button class="settings-button" type="button" data-action="open-settings" aria-label="打开设置">
          <span aria-hidden="true">⚙</span><span>设置</span>
        </button>
      </div>
    </header>
    <main id="screen" class="screen"></main>
    <dialog class="settings-dialog" data-settings-dialog aria-labelledby="settings-title">
      <div class="dialog-header"><div><p class="eyebrow">偏好设置</p><h2 id="settings-title">网站设置</h2></div><button class="icon-button" type="button" data-action="close-settings" aria-label="关闭">×</button></div>
      <div class="settings-group">
        <label for="theme-select"><strong>主题</strong><span>跟随系统会实时响应设备外观变化。</span></label>
        <select id="theme-select" data-theme-select><option value="system">跟随系统</option><option value="light">浅色</option><option value="dark">深色</option></select>
      </div>
      <div class="settings-group">
        <label for="default-scramble-length"><strong>默认打乱次数</strong><span>作为打乱公式生成页的初始值，生成时仍可临时调整。</span></label>
        <input id="default-scramble-length" type="number" inputmode="numeric" min="2" max="50" step="1" value="15" data-default-scramble-length>
      </div>
      <div class="settings-group switch-setting">
        <label for="input-persistence"><strong>保存魔方输入数据</strong><span>开启后，会在此浏览器中分别保存2×2、3×3和4×4的颜色录入进度。</span></label>
        <input id="input-persistence" type="checkbox" role="switch" data-input-persistence>
      </div>
      <div class="danger-zone">
        <div><strong>删除全部本地数据</strong><span>清除本站的魔方输入、计时历史、偏好、旧版数据、数据库和缓存。</span></div>
        <button class="danger-button" type="button" data-action="request-delete-data">删除全部本地数据</button>
      </div>
    </dialog>
    <dialog class="confirm-dialog" data-persistence-dialog aria-labelledby="persistence-confirm-title">
      <h2 id="persistence-confirm-title">是否同时删除已经保存的魔方输入数据？</h2>
      <p>关闭后，本网站不会读取或写入2×2、3×3、4×4输入与4×4方向；当前页面会话不受影响。</p>
      <div class="confirm-actions"><button class="secondary-button" type="button" data-action="cancel-disable-persistence">取消</button><button class="secondary-button" type="button" data-action="disable-persistence-keep">关闭但保留</button><button class="danger-button" type="button" data-action="disable-persistence-delete">关闭并删除</button></div>
    </dialog>
    <dialog class="confirm-dialog danger-confirm" data-delete-dialog aria-labelledby="delete-confirm-title">
      <h2 id="delete-confirm-title">确认删除本站的全部本地数据？</h2>
      <p>将删除2×2、3×3与4×4输入、4×4方向、计时历史记录、求解与播放状态、输入保存开关、主题偏好、旧版遗留键，以及本站的 sessionStorage、IndexedDB 和 Cache Storage 数据。不会删除其他网站的数据。</p>
      <div class="confirm-actions"><button class="secondary-button" type="button" data-action="cancel-delete-data">取消</button><button class="danger-button" type="button" data-action="confirm-delete-data">确认全部删除</button></div>
    </dialog>
    <div class="toast" data-toast role="status"></div>
    <div id="live-region" class="sr-only" aria-live="polite"></div>
  `;
}

export function landingTemplate() {
  return `
    <section class="landing" aria-labelledby="landing-title">
      <div class="hero-copy">
        <p class="eyebrow">在浏览器中记录你的魔方</p>
        <h1 id="landing-title">
          <span class="hero-title-line">看清每一面，</span>
          <span class="hero-title-line hero-title-accent">再开始复原。</span>
        </h1>
        <p class="hero-description">旋转观察 3D 魔方，选择 2×2、3×3 或 4×4，并在浏览器本地完成录入、校验、求解与逐步播放。</p>
        <div class="hero-actions">
          <button class="primary-button hero-button" type="button" data-action="open-order-picker">
            开始求解
            <span aria-hidden="true">→</span>
          </button>
          <button class="primary-button hero-button" type="button" data-action="open-timer">
            计时练习
            <span aria-hidden="true">→</span>
          </button>
          <button class="primary-button hero-button" type="button" data-action="open-tutorial">
            魔方教程
            <span aria-hidden="true">→</span>
          </button>
        </div>
        <div class="hero-meta" aria-label="当前版本能力">
          <span>纯本地运行</span><span>可选本机保存</span><span>支持触摸</span>
        </div>
        <div class="hero-credits" aria-label="制作者信息">
          <div class="hero-credit"><span>设计与审核</span><strong>Joice Sam</strong></div>
          <div class="hero-credit"><span>编程与开发</span><strong>ChatGPT</strong></div>
          <div class="hero-credit"><span>配置与部署</span><strong>Cloudflare</strong></div>
        </div>
      </div>
      <div class="hero-stage">
        <div class="cube-canvas landing-canvas" data-cube-canvas></div>
        <p class="gesture-hint"><span aria-hidden="true">↻</span> 拖动旋转 · 双指缩放</p>
      </div>
    </section>
    <dialog class="order-dialog" data-order-dialog aria-labelledby="order-title">
      <form method="dialog">
        <div class="dialog-header">
          <div>
            <p class="eyebrow">选择魔方类型</p>
            <h2 id="order-title">你要录入哪一种魔方？</h2>
          </div>
          <button class="icon-button" value="cancel" aria-label="关闭">×</button>
        </div>
        <div class="order-grid">
          <button class="order-card active" type="button" data-action="start-2x2">
            <strong>2×2</strong><span>24 贴纸 · cubing.js 本地求解</span><em>开始 →</em>
          </button>
          <button class="order-card active" type="button" data-action="start-3x3">
            <strong>3×3</strong><span>颜色录入已可用</span><em>开始 →</em>
          </button>
          <button class="order-card active" type="button" data-action="start-4x4">
            <strong>4×4</strong><span>96 贴纸 · Threephase 本地求解</span><em>开始 →</em>
          </button>
        </div>
        <p class="dialog-note">2×2、3×3 与 4×4 均提供完整的本地录入、求解和动画流程。</p>
      </form>
    </dialog>
  `;
}

export function entryTemplate(selectedColor, { persistenceEnabled = false, order = 3 } = {}) {
  const palette = paletteTemplate(selectedColor);
  const is2x2 = order === 2;
  const manualTotal = is2x2 ? 24 : 48;
  const perColor = order * order;
  const stateTools = is2x2 ? '' : `<div class="state-tools" aria-label="3×3 状态诊断与交换">
    <button class="secondary-button" type="button" data-action="copy-diagnostics-3x3">复制诊断数据</button>
    <button class="secondary-button" type="button" data-action="export-3x3-json">导出 JSON</button>
    <button class="secondary-button" type="button" data-action="import-3x3-json">导入 JSON</button>
    <input class="sr-only" type="file" accept="application/json,.json" data-import-3x3-json aria-label="选择3×3状态JSON文件">
  </div>`;

  return `
    <section class="entry-layout order-${order}" aria-labelledby="entry-title">
      <div class="entry-stage-panel">
        <div class="entry-heading">
          <button class="back-button" type="button" data-action="home"><span aria-hidden="true">←</span> 返回</button>
          <div>
            <p class="eyebrow">${order}×${order} 颜色录入</p>
            <h1 id="entry-title">${is2x2 ? '填写全部 24 张贴纸' : '逐面填写贴纸颜色'}</h1>
          </div>
        </div>
        <div class="orientation-strip">
          ${is2x2
    ? '<span>任选持握方向</span><span>录入期间保持不变</span>'
    : `<span><i style="--dot:${COLORS.white.hex}"></i>白色朝上</span>
          <span><i style="--dot:${COLORS.green.hex}"></i>绿色朝前</span>
          <span><i style="--dot:${COLORS.red.hex}"></i>红色朝右</span>`}
        </div>
        <div class="cube-canvas entry-canvas" data-cube-canvas></div>
        <div class="stage-footer">
          <p><span aria-hidden="true">↻</span> 拖动旋转视角，轻点贴纸填色</p>
          <p>录入模式只改变视角，不会转动魔方层</p>
        </div>
      </div>

      <aside class="control-panel" aria-label="${order}×${order} 颜色录入控制">
        <div class="panel-section progress-section">
          <div class="section-title-row">
            <div>
              <p class="section-kicker">录入进度</p>
              <h2><span data-filled-count>0</span><small>/ ${manualTotal} 张需填写</small></h2>
            </div>
            <div class="progress-ring" data-progress-ring><span data-progress-percent>0%</span></div>
          </div>
          <div class="progress-track"><span data-progress-bar></span></div>
          <p class="fixed-centers-note">${is2x2 ? '2×2 没有中心块和棱块；任选持握方向后，请保持方向完成全部录入。' : '另有 6 张固定中心贴纸，计入颜色总数，不计入手动录入进度'}</p>
        </div>

        <div class="panel-section">
          <div class="section-label"><h3>1. 选择颜色</h3><span data-selected-label>已选白色</span></div>
          <div class="color-palette">${palette}</div>
        </div>

        <div class="panel-section count-section">
          <div class="section-label"><h3>2. 检查数量</h3><span>每色应为 ${perColor}</span></div>
          <div class="color-counts" data-color-counts></div>
        </div>

        <div class="panel-actions">
          <button class="secondary-button" type="button" data-action="undo" disabled><span aria-hidden="true">↶</span> 撤销</button>
          <button class="secondary-button" type="button" data-action="reset"><span aria-hidden="true">↺</span> ${is2x2 ? '恢复初始状态' : '重置'}</button>
          ${is2x2 ? '<button class="secondary-button wide" type="button" data-action="rescramble-2x2"><span aria-hidden="true">⤨</span> 重新打乱</button>' : ''}
          <button class="secondary-button wide" type="button" data-action="save"><span aria-hidden="true">✓</span> ${persistenceEnabled ? '立即保存到本机' : '开启输入保存'}</button>
        </div>
        ${stateTools}
        <p class="save-status" data-save-status><span></span> ${persistenceEnabled ? '当前进度会自动保存在此浏览器' : '仅保留在当前页面会话，未写入本机'}</p>
        <div class="validation-errors" data-validation-errors hidden></div>
        <div class="solver-status" data-solver-status hidden>
          <span class="solver-spinner" aria-hidden="true"></span>
          <div><strong data-solver-status-title>正在校验状态</strong><small>${is2x2 ? 'cubing.js' : '所有计算'}均在当前浏览器本地完成</small></div>
        </div>
        ${solverDiagnosticsTemplate([], true)}
        <button class="solve-button" type="button" data-action="solve">
          开始校验并求解
          <span>${is2x2 ? 'cubing.js 2×2 算法' : '本地两阶段算法'} →</span>
        </button>
      </aside>
    </section>
  `;
}

export function orientationTemplate4x4({ top = 'white', front = 'green' } = {}) {
  const options = (selected) => COLOR_ORDER.map((color) => (
    `<option value="${color}"${color === selected ? ' selected' : ''}>${COLORS[color].name}</option>`
  )).join('');
  return `
    <section class="orientation-page" aria-labelledby="orientation-title">
      <div class="orientation-card">
        <button class="back-button" type="button" data-action="home"><span aria-hidden="true">←</span> 返回阶数选择</button>
        <p class="eyebrow">4×4 方向配置</p>
        <h1 id="orientation-title">先确定真实魔方的持握方向</h1>
        <p class="hero-description">4×4 没有固定中心块。顶面和正面将决定其余四面的标准配色，也会贯穿录入、求解和动画。</p>
        <div class="orientation-selects">
          <label>顶面颜色<select data-orientation-top>${options(top)}</select></label>
          <label>正面颜色<select data-orientation-front>${options(front)}</select></label>
        </div>
        <div class="orientation-preview" data-orientation-preview></div>
        <div class="validation-errors" data-orientation-errors hidden></div>
        <button class="primary-button" type="button" data-action="confirm-4x4-orientation">确认方向并开始录入 <span>→</span></button>
        <p class="dialog-note">方向不会自动猜测。若之后重新选择，已有 4×4 录入会在确认后单独重置，不影响 3×3 数据。</p>
      </div>
    </section>`;
}

export function entryTemplate4x4(selectedColor, orientation, { persistenceEnabled = false } = {}) {
  const palette = paletteTemplate(selectedColor);
  const orientationItems = ['U', 'F', 'R'].map((face) => {
    const color = orientation.faceToColor[face];
    const label = face === 'U' ? '朝上' : face === 'F' ? '朝前' : '朝右';
    return `<span><i style="--dot:${COLORS[color].hex}"></i>${COLORS[color].name}${label}</span>`;
  }).join('');
  return `
    <section class="entry-layout order-4" aria-labelledby="entry-title">
      <div class="entry-stage-panel">
        <div class="entry-heading">
          <button class="back-button" type="button" data-action="home"><span aria-hidden="true">←</span> 返回</button>
          <div><p class="eyebrow">4×4 颜色录入</p><h1 id="entry-title">填写全部 96 张贴纸</h1></div>
        </div>
        <div class="orientation-strip">${orientationItems}<button type="button" data-action="change-4x4-orientation">重新选择方向</button></div>
        <p class="orientation-instruction">${orientationInstruction4x4(orientation)}</p>
        <div class="cube-canvas entry-canvas" data-cube-canvas></div>
        <div class="stage-footer">
          <p><span aria-hidden="true">↻</span> 拖动旋转视角，轻点贴纸填色</p>
          <p>录入模式只改变视角，不会转动魔方层</p>
        </div>
      </div>

      <aside class="control-panel" aria-label="4×4 颜色录入控制">
        <div class="panel-section progress-section">
          <div class="section-title-row">
            <div><p class="section-kicker">录入进度</p><h2><span data-filled-count>0</span><small>/ 96 张</small></h2></div>
            <div class="progress-ring" data-progress-ring><span data-progress-percent>0%</span></div>
          </div>
          <div class="progress-track"><span data-progress-bar></span></div>
          <p class="fixed-centers-note">4×4 的 24 张中心贴纸均可移动，没有固定中心块。</p>
        </div>
        <div class="panel-section">
          <div class="section-label"><h3>1. 选择颜色</h3><span data-selected-label>已选${COLORS[selectedColor].name}</span></div>
          <div class="color-palette">${palette}</div>
        </div>
        <div class="panel-section count-section">
          <div class="section-label"><h3>2. 检查数量</h3><span>每色应为 16</span></div>
          <div class="color-counts" data-color-counts></div>
        </div>
        <div class="panel-actions">
          <button class="secondary-button" type="button" data-action="undo" disabled><span aria-hidden="true">↶</span> 撤销</button>
          <button class="secondary-button" type="button" data-action="reset"><span aria-hidden="true">↺</span> 重置</button>
          <button class="secondary-button wide" type="button" data-action="save"><span aria-hidden="true">✓</span> ${persistenceEnabled ? '立即保存到本机' : '开启输入保存'}</button>
        </div>
        <div class="state-tools" aria-label="4×4 状态诊断与交换">
          <button class="secondary-button" type="button" data-action="copy-diagnostics">复制诊断数据</button>
          <button class="secondary-button" type="button" data-action="export-4x4-json">导出当前状态 JSON</button>
          <button class="secondary-button" type="button" data-action="import-4x4-json">从 JSON 导入状态</button>
          <input class="sr-only" type="file" accept="application/json,.json" data-import-4x4-json aria-label="选择4×4状态JSON文件">
        </div>
        <p class="save-status" data-save-status><span></span> ${persistenceEnabled ? '当前进度会自动保存在此浏览器' : '仅保留在当前页面会话，未写入本机'}</p>
        <div class="validation-errors" data-validation-errors hidden></div>
        <div class="solver-status" data-solver-status hidden>
          <span class="solver-spinner" aria-hidden="true"></span>
          <div><strong data-solver-status-title>正在检查颜色</strong><small>Threephase 与 min2phase 均在浏览器本地运行</small></div>
          <button class="secondary-button cancel-solve" type="button" data-action="cancel-solve">取消</button>
        </div>
        ${solverDiagnosticsTemplate([], true)}
        <button class="solve-button" type="button" data-action="solve">
          检查并求解 4×4
          <span>本地 Worker · 支持 parity →</span>
        </button>
      </aside>
    </section>`;
}

export function playbackTemplate(solution) {
  const formula = solution.moves.map((move, index) => (
    `<span class="formula-move" data-formula-step="${index + 1}">${move}</span>`
  )).join(' ');
  return `
    <section class="playback-layout order-${solution.order ?? 3}" aria-labelledby="playback-title">
      <div class="playback-stage-panel">
        <div class="entry-heading playback-heading">
          <button class="back-button" type="button" data-action="back-edit"><span aria-hidden="true">←</span> 修改颜色</button>
          <div>
            <p class="eyebrow">${solution.order ?? 3}×${solution.order ?? 3} 还原演示</p>
            <h1 id="playback-title">按照真实解法逐步复原</h1>
          </div>
        </div>
        <div class="cube-canvas playback-canvas" data-cube-canvas></div>
        <div class="stage-footer">
          <p><span aria-hidden="true">↻</span> 播放时仍可拖动旋转视角</p>
          <p>手势不会转动单独层</p>
        </div>
      </div>

      <aside class="playback-panel" aria-label="还原播放控制">
        <div class="playback-summary">
          <div><p class="section-kicker">当前进度</p><h2><span data-playback-step>0</span><small> / ${solution.moveCount}</small></h2></div>
          <div class="solve-metric"><strong>${solution.moveCount}</strong><span>总动作</span></div>
          <div class="solve-metric"><strong>${solution.durationMs}</strong><span>毫秒求解</span></div>
        </div>

        <div class="move-focus">
          <div><span>当前动作</span><strong data-current-move>准备</strong></div>
          <div class="move-explanation"><span data-move-description>从录入状态开始</span><small>下一动作：<b data-next-move>${solution.moves[0] ?? '完成'}</b></small></div>
        </div>

        <div class="formula-panel">
          <div class="section-label"><h3>完整还原公式</h3><span>${solution.moveCount} 步</span></div>
          <div class="formula-text" data-formula>${formula || '<span>当前状态已经复原，无需转动</span>'}</div>
        </div>

        ${solution.diagnostics?.length ? solverDiagnosticsTemplate(solution.diagnostics) : ''}

        <div class="transport-controls">
          <button type="button" data-action="first" aria-label="跳到第一步">|←</button>
          <button type="button" data-action="previous">上一步</button>
          <button class="play-button" type="button" data-action="play">播放</button>
          <button type="button" data-action="pause" hidden>暂停</button>
          <button type="button" data-action="next">下一步</button>
          <button type="button" data-action="last" aria-label="跳到最后一步">→|</button>
        </div>

        <div class="playback-options">
          <button class="secondary-button" type="button" data-action="restart"><span aria-hidden="true">↺</span> 重新开始</button>
          <label>播放速度
            <select data-playback-speed aria-label="播放速度">
              ${[0.1, 0.25, 0.5, 1, 1.5, 2].map((speed) => `<option value="${speed}"${Number(solution.playbackSpeed ?? 1) === speed ? ' selected' : ''}>${speed}×</option>`).join('')}
            </select>
          </label>
        </div>
        <button class="secondary-button wide playback-home" type="button" data-action="home">返回阶数选择</button>
      </aside>
    </section>
  `;
}
